import { useTranslation as useI18nextTranslation } from 'react-i18next';
import type { SupportedLanguage } from './config';

/**
 * Custom wrapper around react-i18next's useTranslation hook
 * Provides type safety and convenience methods for our application
 */
export const useTranslation = (ns?: string | string[], options?: any) => {
  const { t, i18n, ready } = useI18nextTranslation(ns, options);

  return {
    t,
    i18n,
    ready,

    // Convenience properties
    currentLanguage: i18n.language as SupportedLanguage,
    isRTL: false, // None of our supported languages use RTL

    // Convenience methods
    changeLanguage: (lng: SupportedLanguage) => i18n.changeLanguage(lng),

    // Format helpers
    formatDate: (date: Date | string, format: 'short' | 'long' | 'time' = 'short'): string => {
      const d = typeof date === 'string' ? new Date(date) : date;

      const options: Intl.DateTimeFormatOptions =
        format === 'short' ? { year: 'numeric', month: '2-digit', day: '2-digit' } :
        format === 'long' ? { year: 'numeric', month: 'long', day: 'numeric' } :
        { hour: '2-digit', minute: '2-digit' };

      return new Intl.DateTimeFormat(i18n.language, options).format(d);
    },

    formatCurrency: (amount: number, currency: string = 'BAM'): string => {
      return new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency,
      }).format(amount);
    },

    formatNumber: (value: number, decimals: number = 2): string => {
      return new Intl.NumberFormat(i18n.language, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value);
    },
  };
};

export default useTranslation;
